import React, { FC, Fragment, useEffect } from 'react';
import { FormControlLabel, Grid, Tabs, Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';
import LinkIcon from '@material-ui/icons/Link';
import SubjectRoundedIcon from '@material-ui/icons/SubjectRounded';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import { ContentInput } from 'api';
import { useProtectedClient, useCapabilities, useNetworkCapabilities } from 'hooks';
import { Capability } from 'components';
import { ComposeLayout, ComposeTab, ComposeTabBody } from 'features/compose';

import {
  ContentTagging,
  Disclosures,
  ScheduleExpirePost,
  UrlTracking,
  FormButtons,
  LinkForm,
  PinPost,
  Slack,
  TextForm,
  ImageVideoForm,
  MarkImportant,
  MicrosoftTeams,
  SingleGroupDropdown,
  GroupSelection,
  ShareCopyWidget,
} from '../components';
import { usePostContext } from '../PostContext';
import { PostOption } from '../types';
import { Switch } from 'features/posting/components';
import { ContentProvider } from 'providers';
import { UploadCountProvider } from '../components/ImageVideo/UploadCountProvider';
import useReleases from 'hooks/useReleases';

const useStyles = makeStyles(theme => ({
  '@global html, body': {
    backgroundColor: theme.palette.common.white,
  },
  tabIcon: {
    marginRight: theme.spacing(1),
  },
  subtitle: {
    margin: theme.spacing(1.5, 0, -1, 0),
  },
  title: {
    margin: theme.spacing(0, 0, -1, 0),
  },
  switch: {
    top: 0,
    right: 0,
    marginLeft: 0,
  },
  labelPlacementStart: {
    marginLeft: 0,
  },
}));

const Post: FC = ({ children }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext<ContentInput>();
  const { t } = useTranslation();
  const { isAdmin, isModerator, isContributor } = useCapabilities();
  const { hasSlack, hasMSTeams } = useNetworkCapabilities();
  const { moderator_can_mark_as_important } = useProtectedClient();
  const releases = useReleases();
  const { state, dispatch } = usePostContext();

  const hasModeratorWidgets = isAdmin || (isModerator && state.moderatorMode);
  const hasContributorWidgets = hasModeratorWidgets || isContributor;
  const hasMarkImportant = isAdmin || (hasModeratorWidgets && moderator_can_mark_as_important);
  const hasContentTagging = releases.includes('UseContentTagging');

  const isLinkForm = state.option === PostOption.Link;
  const isEdit = window.location.href.includes('/post/edit/');

  useEffect(() => {
    if (isEdit) {
      dispatch({ type: 'editing', value: isEdit });
    }
  }, []);

  function handleChangeTab(event: unknown, value: PostOption) {
    dispatch({ type: 'option', value });
    dispatch({ type: 'slack', value: false });
    dispatch({ type: 'msteams', value: false });
    setFieldValue('slack_channels', []);
    setFieldValue('ms_teams_channels', []);

    setFieldValue('content_object.media', null);
    setFieldValue('content_object.url', '');
    setFieldValue('content_object.photos', []);

    setFieldValue('content_object.url_metadata_override.title', '');
    setFieldValue('content_object.url_metadata_override.description', '');
    setFieldValue('url_param_values', []);

    dispatch({ type: 'validUrl', value: false });
    dispatch({ type: 'clearPhotoIndexes' });
    if (values.content_object.photos.length) {
      dispatch({ type: 'updatePhotoIndexes', value: 0 });
    }
  }

  function renderTabs() {
    return (
      <Tabs
        value={state.option}
        onChange={handleChangeTab}
        textColor="primary"
        indicatorColor="primary"
        variant="fullWidth"
      >
        <ComposeTab
          value={PostOption.Link}
          disabled={isEdit && state.option !== PostOption.Link}
          label={
            <Fragment>
              <LinkIcon className={classes.tabIcon} />
              {t('common:link')}
            </Fragment>
          }
        />
        <ComposeTab
          value={PostOption.ImageVideo}
          disabled={isEdit && state.option !== PostOption.ImageVideo}
          label={
            <Fragment>
              <AddPhotoAlternateIcon className={classes.tabIcon} />
              {t('posting:imageOrVideo')}
            </Fragment>
          }
        />
        <ComposeTab
          value={PostOption.Text}
          disabled={isEdit && state.option !== PostOption.Text}
          label={
            <Fragment>
              <SubjectRoundedIcon className={classes.tabIcon} />
              {t('common:Text')}
            </Fragment>
          }
        />
      </Tabs>
    );
  }

  function renderPrimaryGroupDropDown() {
    const preserveLegacyBehaviour = isEdit && !state.requiresApproval;

    return <SingleGroupDropdown disabled={preserveLegacyBehaviour} />;
  }

  function handleToggleRepost() {
    dispatch({ type: 'repost', value: !state.repost });
    dispatch({ type: 'moderatorMode', value: !state.repost });
  }

  function renderHeader() {
    const showMultiGroupHeader = values.primary_group_id && (state.repost || isAdmin);

    return (
      <Fragment>
        <Grid item classes={{ item: classes.title }}>
          <Typography variant="h4">
            {isEdit ? t('posting:editPost') : t('posting:createPost')}
          </Typography>
        </Grid>

        <Grid item>
          {isModerator && !isAdmin && (
            <Grid item>
              <FormControlLabel
                value={state.repost}
                label={t('posting:toggleRepost')}
                labelPlacement="start"
                classes={{ labelPlacementStart: classes.labelPlacementStart }}
                control={
                  <Switch
                    data-testid="post-radio-toggleRepost"
                    classes={{ root: classes.switch }}
                    checked={state.repost}
                    color="primary"
                    onChange={handleToggleRepost}
                  />
                }
              />
            </Grid>
          )}
        </Grid>

        <Grid container alignItems={'center'}>
          <Grid item xs={5}>
            {renderPrimaryGroupDropDown()}
          </Grid>
          {showMultiGroupHeader && (
            <Capability name="Post.Editing.Function.Edit">
              <Grid item xs={2}>
                <Typography variant="body2" align="center">
                  {`${t('posting:repostTo')}:`}
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <GroupSelection />
              </Grid>
            </Capability>
          )}
        </Grid>

        <Grid item classes={{ item: classes.subtitle }}>
          <Typography variant="subtitle1">{t('posting:createPostSubhead')}</Typography>
        </Grid>
      </Fragment>
    );
  }

  function renderBody() {
    return (
      <ComposeTabBody>
        {state.option === PostOption.Link && (
          <LinkForm contributor={hasContributorWidgets} moderator={hasModeratorWidgets} />
        )}
        {state.option === PostOption.ImageVideo && (
          <ImageVideoForm contributor={hasContributorWidgets} moderator={hasModeratorWidgets} />
        )}
        {state.option === PostOption.Text && (
          <TextForm contributor={hasContributorWidgets} moderator={hasModeratorWidgets} />
        )}
      </ComposeTabBody>
    );
  }

  return (
    <UploadCountProvider>
      <ComposeLayout header={renderHeader()} tabs={renderTabs()} body={renderBody()}>
        {hasContributorWidgets && (
          <ContentProvider value={values}>
            <Grid item container>
              <ShareCopyWidget />
            </Grid>
          </ContentProvider>
        )}

        {hasMarkImportant && (
          <Grid item container>
            <MarkImportant />
          </Grid>
        )}

        {hasModeratorWidgets && (
          <Grid item container>
            <PinPost />
          </Grid>
        )}

        {hasModeratorWidgets && (
          <Capability name="Post.Create.Function.ScheduleExpire">
            <Grid item container>
              <ScheduleExpirePost />
            </Grid>
          </Capability>
        )}

        {hasModeratorWidgets && (
          <Capability name="Post.Create.Function.ApplyDisclosures">
            <Grid item container>
              <Disclosures />
            </Grid>
          </Capability>
        )}

        {hasContentTagging && (
          <Grid item container>
            <ContentTagging />
          </Grid>
        )}

        {isLinkForm && hasModeratorWidgets && (
          <Capability name="Post.Create.Function.ApplyUtmParameters">
            <UrlTracking />
          </Capability>
        )}

        {state.option !== PostOption.Text && hasMSTeams && (
          <Grid item container>
            <MicrosoftTeams />
          </Grid>
        )}

        {state.option !== PostOption.Text && hasSlack && (
          <Grid item container>
            <Slack />
          </Grid>
        )}

        {children}

        <FormButtons />
      </ComposeLayout>
    </UploadCountProvider>
  );
};

export default Post;
